/**
 * Значок «?» рядом с термином: нажатие открывает короткое объяснение.
 *
 * Всплывающая подсказка при наведении здесь не подходит — приложение мобильное,
 * наведения на телефоне нет, а подсказка «по долгому нажатию» не обнаруживается
 * без обучения. Шторка же уже используется во всём приложении, закрывается
 * свайпом и не перекрывает контекст.
 *
 * Сам текст живёт в `shared/config/glossary.ts`: один термин объясняется
 * одинаково везде, где встречается.
 */

import { HelpCircle } from 'lucide-react';
import { useState } from 'react';

import type { GlossaryEntry } from '@/shared/config/glossary';
import { cn } from '@/shared/lib/cn';

import { Sheet } from './Sheet';

interface HelpTipProps {
  entry: GlossaryEntry;
  className?: string;
}

export function HelpTip({ entry, className }: HelpTipProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        // Зона нажатия вынесена за пределы значка отрицательным полем: сам знак
        // 13 px, а под палец нужно 44 px. Без этого попасть по нему можно было
        // бы только ногтем.
        className={cn(
          'relative -m-2.5 inline-flex size-11 shrink-0 items-center justify-center',
          'text-subtle transition-colors active:text-content',
          className,
        )}
        onClick={(event) => {
          // Термин часто стоит внутри карточки, которая сама открывается по
          // нажатию: без остановки всплытия «?» открывал бы карточку.
          event.stopPropagation();
          setOpen(true);
        }}
        aria-label={`Что такое «${entry.term}»`}
      >
        <HelpCircle size={14} aria-hidden />
      </button>

      {open && (
        <Sheet open onClose={() => setOpen(false)} title={entry.term}>
          <p className="pb-2 text-[15px] leading-relaxed text-muted">{entry.text}</p>
        </Sheet>
      )}
    </>
  );
}
